import React, { useState, useEffect, useCallback } from 'react';
import { cardsApi, tournamentsApi } from '../services/api';
import { Card, Tournament } from '../types';

const Cards: React.FC = () => {
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTournament, setSelectedTournament] = useState<string>('');
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [tournamentPrices, setTournamentPrices] = useState<Record<string, number>>({});
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'set' | 'rarity'>('name');
  const cardsPerPage = 60;

  const loadTournaments = async () => {
    try {
      const response = await tournamentsApi.getAll();
      setTournaments(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load tournaments:', error);
    }
  };

  const sortCards = useCallback((cards: Card[]) => {
    return [...cards].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
        case 'price':
          const priceA = tournamentPrices[a.id] ?? a.cheapest_price ?? 0;
          const priceB = tournamentPrices[b.id] ?? b.cheapest_price ?? 0;
          return priceB - priceA; // Descending order (highest price first)
        case 'set':
          return a.set_code.localeCompare(b.set_code, undefined, { numeric: true, sensitivity: 'base' });
        case 'rarity':
          const rarityOrder = { 'mythic': 4, 'rare': 3, 'uncommon': 2, 'common': 1 };
          const rarityA = rarityOrder[a.rarity as keyof typeof rarityOrder] || 0;
          const rarityB = rarityOrder[b.rarity as keyof typeof rarityOrder] || 0;
          return rarityB - rarityA; // Descending order (highest rarity first)
        default:
          return 0;
      }
    });
  }, [sortBy, tournamentPrices]);

  const loadTournamentPrices = useCallback(async (cardIds: string[]) => {
    if (!selectedTournament || cardIds.length === 0) {
      setTournamentPrices({});
      return;
    }

    try {
      const response = await cardsApi.getTournamentPrices(selectedTournament, cardIds);
      const priceMap: Record<string, number> = {};
      
      // Create a mapping from oracle_id to card_id for the current cards
      const oracleToCardMap: Record<string, string> = {};
      cards.forEach(card => {
        oracleToCardMap[card.oracle_id] = card.id;
      });
      
      response.data.forEach((price: any) => {
        // Use EUR price as primary, fallback to USD, then TIX
        const cardPrice = price.eur_price || price.usd_price || price.tix_price || 0;
        // Map from oracle_id to card_id
        const cardId = oracleToCardMap[price.oracle_id];
        if (cardId) {
          priceMap[cardId] = cardPrice;
        }
      });
      setTournamentPrices(priceMap);
    } catch (error) {
      console.error('Failed to load tournament prices:', error);
      setTournamentPrices({});
    }
  }, [selectedTournament, cards]);

  const loadCards = useCallback(async (page = 1, reset = true) => {
    try {
      setLoading(true);
      const offset = (page - 1) * cardsPerPage;
      const response = await cardsApi.search(
        searchQuery, 
        offset, 
        selectedTournament || undefined
      );
      
      // Sort cards based on selected sort option
      const sortedCards = sortCards(response.data);
      
      if (reset) {
        setCards(sortedCards);
      } else {
        setCards(prev => [...prev, ...sortedCards]);
      }
      
      // Calculate total pages based on response
      // Note: This is a simplified calculation. In a real app, the API should return total count
      const estimatedTotalPages = Math.ceil(response.data.length / cardsPerPage);
      setTotalPages(estimatedTotalPages);

      // Load tournament prices if a tournament is selected
      if (selectedTournament && response.data.length > 0) {
        const cardIds = response.data.map((card: Card) => card.id);
        await loadTournamentPrices(cardIds);
      }
    } catch (error) {
      console.error('Failed to load cards:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedTournament, cardsPerPage, loadTournamentPrices, sortCards]);

  useEffect(() => {
    loadTournaments();
    setLoading(false); // Set loading to false after tournaments are loaded
  }, []);

  const handleSearch = async () => {
    setCurrentPage(1);
    setHasSearched(true);
    await loadCards(1, true);
  };

  const handleTournamentChange = async (tournamentId: string) => {
    setSelectedTournament(tournamentId);
    setCurrentPage(1);
    setTournamentPrices({}); // Clear previous tournament prices
    if (searchQuery) {
      await loadCards(1, true);
    }
  };

  const handleSortChange = (newSortBy: 'name' | 'price' | 'set' | 'rarity') => {
    setSortBy(newSortBy);
    // Re-sort existing cards
    setCards(prev => sortCards(prev));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const goToPage = async (page: number) => {
    setCurrentPage(page);
    await loadCards(page, true);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading cards...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Cards</h1>
        
        <div className="mb-6">
          <div className="flex gap-4 mb-4">
            <input
              type="text"
              placeholder="Search cards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
          </div>
          
          <div className="flex gap-4 flex-wrap">
            <select
              value={selectedTournament}
              onChange={(e) => handleTournamentChange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Cards</option>
              {tournaments.map((tournament) => (
                <option key={tournament.id} value={tournament.id}>
                  {tournament.name}
                </option>
              ))}
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value as 'name' | 'price' | 'set' | 'rarity')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="name">Sort by Name</option>
              <option value="price">Sort by Price</option>
              <option value="set">Sort by Set</option>
              <option value="rarity">Sort by Rarity</option>
            </select>
            
            {selectedTournament && (
              <span className="px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm">
                Showing legal cards for selected tournament
              </span>
            )}
            
            <span className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm">
              Sorted by: {sortBy === 'name' ? 'Name' : 
                         sortBy === 'price' ? 'Price (High to Low)' :
                         sortBy === 'set' ? 'Set' :
                         sortBy === 'rarity' ? 'Rarity (High to Low)' : 'Name'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {cards.map((card) => {
            // Use tournament price if available, otherwise fall back to cheapest_price
            const displayPrice = tournamentPrices[card.id] ?? card.cheapest_price;
            return (
              <div key={card.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                {/* Card Image */}
                {card.image_uri ? (
                  <div className="aspect-[488/680] bg-gray-100 flex items-center justify-center relative">
                    {imageLoadingStates[card.id] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                    <img
                      src={card.image_uri}
                      alt={card.name}
                      className={`w-full h-full object-cover transition-opacity duration-200 ${
                        imageLoadingStates[card.id] ? 'opacity-0' : 'opacity-100'
                      }`}
                      onLoad={() => {
                        setImageLoadingStates(prev => ({ ...prev, [card.id]: false }));
                      }}
                      onError={(e) => {
                        setImageLoadingStates(prev => ({ ...prev, [card.id]: false }));
                        // Hide image if it fails to load
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoadStart={() => {
                        setImageLoadingStates(prev => ({ ...prev, [card.id]: true }));
                      }}
                    />
                  </div>
                ) : (
                  <div className="aspect-[488/680] bg-gray-100 flex items-center justify-center">
                    <div className="text-gray-400 text-center p-4">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm">No Image</p>
                    </div>
                  </div>
                )}
                
                {/* Card Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2">{card.name}</h3>
                  
                  {/* Set and Rarity Info */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                      {card.set_code?.toUpperCase()}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      card.rarity === 'mythic' ? 'bg-purple-100 text-purple-800' :
                      card.rarity === 'rare' ? 'bg-yellow-100 text-yellow-800' :
                      card.rarity === 'uncommon' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {card.rarity}
                    </span>
                  </div>
                  
                  {/* Price */}
                  <div className="flex items-center justify-between">
                    <p className="text-green-600 font-bold text-lg">
                      €{displayPrice?.toFixed(2) || 'N/A'}
                    </p>
                    {selectedTournament && tournamentPrices[card.id] && (
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                        Tournament
                      </span>
                    )}
                  </div>
                  
                  {/* Additional Info */}
                  {(card.mana_cost || card.type_line) && (
                    <div className="mt-2 text-sm text-gray-600">
                      {card.mana_cost && (
                        <div className="mb-1">
                          <span className="font-medium">Cost:</span> {card.mana_cost}
                        </div>
                      )}
                      {card.type_line && (
                        <div className="line-clamp-1">
                          <span className="font-medium">Type:</span> {card.type_line}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {cards.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              {hasSearched ? 'No cards found.' : 'Enter a card name to search'}
            </p>
          </div>
        )}

        {cards.length > 0 && totalPages > 1 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    disabled={loading}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cards;
